import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
// mui components
import { Box, FormControlLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material';
// components
import Checkbox from 'components/checkbox';
import StepField from './step-field';
// hooks
import { IArtistProfile } from 'hooks/swrRequests/useArtistProfiles';
// config
import {
  GIFT_ADDRESS_FIELDS,
  GIFT_ADDRESS_OTHER,
  GIFT_ADDRESS_RESIDENTIAL,
  ProfileScalarKey,
  TAX_NUMBER_FIELD,
} from '../steps';
// icons
import { Gift } from 'lucide-react';
// styles
import { colors } from 'styles/colors';
import { useTranslation } from 'react-i18next';

// the stored values are the labels — they are also the Airtable select's option names
const ADDRESS_TYPE_OPTIONS = [GIFT_ADDRESS_RESIDENTIAL, GIFT_ADDRESS_OTHER];

interface OwnProps {
  profile: IArtistProfile;
  defaultValues: Record<string, string>;
}

/**
 * Welcome gift consent (unticked by default) with a choice of shipping
 * address. The server clears the address type and the alternate address
 * whenever they are not in use. The gift fields decide their own visibility
 * (`showWhen`): state only for US/AU/CA, tax id only for Brazil.
 */
const WelcomeGiftBlock: React.FC<OwnProps> = ({ profile, defaultValues }) => {
  const { t } = useTranslation();
  const { control, watch } = useFormContext();

  const optedIn = watch('welcomeGift') === 'Yes';
  const useOtherAddress = optedIn && watch('welcomeGiftAddressType') === GIFT_ADDRESS_OTHER;

  return (
    <Box>
      <Box component="label" display="flex" alignItems="center" sx={{ cursor: 'pointer' }}>
        <Controller
          name="welcomeGift"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Checkbox
              checked={value === 'Yes'}
              onChange={(event) => onChange(event.target.checked ? 'Yes' : 'No')}
            />
          )}
        />
        <Typography sx={{ fontSize: 16, color: colors.text.light.title, mr: 1 }}>
          {t('Send me a welcome gift')}
        </Typography>
        <Gift size={16} color="#8C8C8C" aria-hidden />
      </Box>
      {optedIn && (
        <Controller
          name="welcomeGiftAddressType"
          control={control}
          render={({ field: { value, onChange } }) => (
            <RadioGroup
              aria-label={t('Gift address')}
              // the server defaults an opt-in without a type to the residential address
              value={value === GIFT_ADDRESS_OTHER ? GIFT_ADDRESS_OTHER : GIFT_ADDRESS_RESIDENTIAL}
              onChange={(event) => onChange(event.target.value)}
              sx={{ ml: 3.5 }}
            >
              {ADDRESS_TYPE_OPTIONS.map((option) => (
                <FormControlLabel
                  key={option}
                  value={option}
                  control={
                    <Radio
                      size="small"
                      sx={{ color: '#8C8C8C', '&.Mui-checked': { color: '#141414' } }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: 16, color: colors.text.light.title }}>
                      {t(option)}
                    </Typography>
                  }
                />
              ))}
            </RadioGroup>
          )}
        />
      )}
      {useOtherAddress && (
        <Stack
          component="fieldset"
          spacing={2}
          sx={{
            mt: 2,
            mx: 0,
            p: 2.5,
            // fieldsets default to min-content width, which overflows narrow screens
            minWidth: 0,
            border: '1px solid #E9E9E9',
            borderRadius: '12px',
            bgcolor: '#FAFAFA',
          }}
        >
          {/* names the group for screen readers; the fields carry the visible labels */}
          <Box
            component="legend"
            sx={{
              position: 'absolute',
              width: '1px',
              height: '1px',
              p: 0,
              overflow: 'hidden',
              clip: 'rect(0 0 0 0)',
              whiteSpace: 'nowrap',
            }}
          >
            {t('Gift address')}
          </Box>
          {GIFT_ADDRESS_FIELDS.map((field) => (
            <StepField
              key={field.key}
              field={field}
              defaultValue={defaultValues[field.key]}
              storedValue={profile[field.key as ProfileScalarKey]}
            />
          ))}
        </Stack>
      )}
      {optedIn && (
        <Box sx={{ mt: 2 }}>
          <StepField
            field={TAX_NUMBER_FIELD}
            defaultValue={defaultValues[TAX_NUMBER_FIELD.key]}
            storedValue={profile.taxNumber}
          />
        </Box>
      )}
    </Box>
  );
};

export default WelcomeGiftBlock;
